import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Plus } from 'lucide-react';
import { useCustomFields, type CustomField } from '@/hooks/useCustomFields';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';

const CustomFieldsTab: React.FC = () => {
  const { fields, loading, createField, updateField, deleteField } = useCustomFields();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'text' as CustomField['field_type'],
    is_active: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingField) {
      const success = await updateField(editingField.id, { ...formData, is_required: false, options: [] });
      if (success) {
        setDialogOpen(false);
        resetForm();
      }
    } else {
      const created = await createField({ ...formData, is_required: false, options: [] });
      if (created) {
        setDialogOpen(false);
        resetForm();
      }
    }
  };

  const handleEdit = (field: CustomField) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      field_type: 'text',
      is_active: field.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este campo?')) {
      await deleteField(id);
    }
  };

  const resetForm = () => {
    setEditingField(null);
    setFormData({
      name: '',
      field_type: 'text',
      is_active: true
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-neutral-50">Campos Personalizados</h2>
          <p className="text-sm text-neutral-400">Gerencie campos personalizados para contatos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#E4E4E7] text-neutral-900 hover:bg-[#D4D4D8]">
              <Plus className="w-4 h-4 mr-2" />
              Novo Campo
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingField ? 'Editar Campo' : 'Novo Campo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="name">Nome do Campo</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-neutral-50 min-h-[36px] border focus:ring-white border-[#3F3F46]"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Campo ativo</Label>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#E4E4E7] text-neutral-900">
                    {editingField ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#3F3F464D] rounded-lg bg-[#18181B]">
        {loading ? (
          <div className="p-8 text-center text-neutral-400">Carregando...</div>
        ) : fields.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">Nenhum campo personalizado criado</div>
        ) : (
          <div className="divide-y divide-[#3F3F464D]">
            {fields.map((field) => (
              <div key={field.id} className="p-4 flex items-center justify-between hover:bg-neutral-800/50">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-neutral-50">{field.name}</h3>
                    {!field.is_active && (
                      <span className="text-xs px-2 py-1 rounded bg-red-900/30 text-red-500">
                        Inativo
                      </span>
                    )}
                  </div>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex justify-center items-center rounded-[8px] border-[1px] border-[#3F3F464D] bg-neutral-600 w-[34px] h-[34px]">
                      <EllipsisVertical size={20} color="#FAFAFA" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-[#18181B] p-1 w-[95px] flex flex-col gap-1 border-[#3F3F464D] border">
                    <button
                      onClick={() => handleEdit(field)}
                      className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(field.id)}
                      className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] text-red-400"
                    >
                      Excluir
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFieldsTab;
